import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BatteryCharging,
  Bell,
  Calendar,
  Car,
  CheckCircle2,
  ChevronRight,
  Clock,
  Compass,
  Cpu,
  Crosshair,
  FileText,
  Fuel,
  Gauge,
  Grid,
  History,
  Info,
  Key,
  Layers,
  Lock,
  LogOut,
  MapPin,
  Maximize2,
  Menu,
  Minimize2,
  MoreVertical,
  Navigation,
  Pause,
  Phone,
  Play,
  Power,
  Radio,
  Repeat,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  Smartphone,
  TrafficCone,
  Truck,
  Unlock,
  User,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import { API_URL } from '../config/api';
import { getCurrentUser, clearAuthSession } from '../utils/auth';
import './CustomerPanel.css';

// Format Date & Time (e.g. 15 Jan 2026, 02:30 PM)
const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  return (
    d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) +
    ', ' +
    d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  );
};

// Format Date only (e.g. 15 Jan 2027)
const formatDateOnly = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getTimeAgo = (dateStr) => {
  if (!dateStr) return 'Just now';
  const diffSec = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec} sec ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day ago`;
};

// Calculate Subscription Expiry Remaining Days
const getExpiryStatus = (expiryDateStr) => {
  if (!expiryDateStr) return { label: 'Active Plan', days: 365, color: '#16a34a', bg: '#dcfce7' };
  const expiry = new Date(expiryDateStr);
  const now = new Date();
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) {
    return { label: 'Expired (Renew Now)', days: diffDays, color: '#dc2626', bg: '#fee2e2' };
  }
  if (diffDays <= 30) {
    return { label: `${diffDays} Days Left (Renew Soon)`, days: diffDays, color: '#d97706', bg: '#fef3c7' };
  }
  return { label: `${diffDays} Days Left`, days: diffDays, color: '#16a34a', bg: '#dcfce7' };
};

// Animated Vehicle Marker with Direction Needle & Speed Badge
const createCarIcon = (status = 'moving', speed = 0, heading = 0, plate = '') => {
  const isMoving = status === 'moving' || Number(speed) > 3;
  const color = isMoving ? '#10b981' : '#ef4444';
  const speedText = isMoving ? `${Number(speed).toFixed(0)} km/h` : 'Stopped';

  return L.divIcon({
    className: 'custom-gps-leaflet-icon',
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer;">
        <div style="
          background: #0f172a;
          color: #ffffff;
          font-size: 11px;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 6px;
          border: 1.5px solid ${color};
          white-space: nowrap;
          box-shadow: 0 4px 14px rgba(0,0,0,0.35);
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
          letter-spacing: 0.5px;
        ">
          <span>${plate || 'DL 01 AA 9999'}</span>
          <span style="background: ${isMoving ? '#10b981' : '#ef4444'}; color: #fff; font-size: 9.5px; padding: 1px 5px; border-radius: 4px; font-weight: 900;">
            ${speedText}
          </span>
        </div>

        <div style="
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #ffffff;
          border: 3.5px solid ${color};
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 20px rgba(0,0,0,0.25);
          transform: rotate(${heading || 0}deg);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        ">
          <div style="
            position: absolute;
            top: -6px;
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 7px solid ${color};
          "></div>

          <svg width="22" height="22" viewBox="0 0 24 24" fill="${isMoving ? '#10b981' : '#ef4444'}" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C2.1 10.8 2 11 2 11.3V16c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2" fill="#0f172a"/>
            <path d="M9 17h6"/>
            <circle cx="17" cy="17" r="2" fill="#0f172a"/>
          </svg>
        </div>
      </div>
    `,
    iconSize: [80, 80],
    iconAnchor: [40, 60],
    popupAnchor: [0, -60]
  });
};

// Premium HD Map Tile Providers
const MAP_LAYERS = {
  voyager: {
    name: 'HD Street',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    subdomains: ['a', 'b', 'c', 'd'],
    maxZoom: 20,
    attribution: '&copy; CartoDB &copy; OpenStreetMap'
  },
  osm: {
    name: 'OSM',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    subdomains: ['a', 'b', 'c'],
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap contributors'
  },
  dark: {
    name: 'Night Mode',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    subdomains: ['a', 'b', 'c', 'd'],
    className: 'soft-night-map-tiles',
    maxZoom: 20,
    attribution: '&copy; Soft Night Map'
  },
  satellite: {
    name: 'Satellite',
    url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    maxZoom: 20,
    attribution: '&copy; Google Hybrid Satellite'
  }
};

const normalizeWhatsAppNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length === 10 ? `91${digits}` : digits;
};

// Speedometer Gauge Arc SVG Component
function SpeedGauge({ speed = 0 }) {
  const numSpeed = Math.max(0, Number(speed) || 0);
  const maxSpeed = 120;
  const percentage = Math.min(numSpeed / maxSpeed, 1);
  const circumference = 120;
  const strokeDashoffset = circumference - percentage * circumference;
  const color = numSpeed > 60 ? '#ef4444' : numSpeed > 35 ? '#f59e0b' : '#10b981';

  return (
    <div className="speed-gauge-wrap">
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle
          cx="34"
          cy="34"
          r="26"
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset="0"
          strokeLinecap="round"
          transform="rotate(135 34 34)"
        />
        <circle
          cx="34"
          cy="34"
          r="26"
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(135 34 34)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="speed-gauge-text">
        <span className="speed-number">{Math.round(numSpeed)}</span>
        <span className="speed-unit">km/h</span>
      </div>
    </div>
  );
}

// Vehicle Realistic Visual Component
function VehicleVisual({ type = 'SUV', status = 'online' }) {
  const normType = String(type || '').toLowerCase();

  if (normType.includes('truck') || normType.includes('lorry') || normType.includes('tata')) {
    return (
      <div className="vehicle-visual-box truck">
        <span className={`live-status-dot ${status === 'online' ? 'online' : 'offline'}`} />
        <svg viewBox="0 0 100 70" width="100%" height="100%">
          <rect x="10" y="15" width="45" height="35" rx="4" fill="#38bdf8" />
          <rect x="58" y="22" width="30" height="28" rx="5" fill="#0284c7" />
          <rect x="68" y="26" width="16" height="12" rx="2" fill="#e0f2fe" />
          <circle cx="25" cy="52" r="8" fill="#0f172a" stroke="#94a3b8" strokeWidth="2" />
          <circle cx="45" cy="52" r="8" fill="#0f172a" stroke="#94a3b8" strokeWidth="2" />
          <circle cx="76" cy="52" r="8" fill="#0f172a" stroke="#94a3b8" strokeWidth="2" />
          <rect x="5" y="48" width="90" height="4" fill="#64748b" rx="2" />
        </svg>
      </div>
    );
  }

  if (normType.includes('bus')) {
    return (
      <div className="vehicle-visual-box bus">
        <span className={`live-status-dot ${status === 'online' ? 'online' : 'offline'}`} />
        <svg viewBox="0 0 100 70" width="100%" height="100%">
          <rect x="10" y="18" width="80" height="32" rx="7" fill="#f43f5e" />
          <rect x="18" y="24" width="14" height="10" rx="2" fill="#ffe4e6" />
          <rect x="36" y="24" width="14" height="10" rx="2" fill="#ffe4e6" />
          <rect x="54" y="24" width="14" height="10" rx="2" fill="#ffe4e6" />
          <rect x="72" y="24" width="12" height="14" rx="2" fill="#ffe4e6" />
          <circle cx="28" cy="52" r="8" fill="#0f172a" stroke="#cbd5e1" strokeWidth="2" />
          <circle cx="74" cy="52" r="8" fill="#0f172a" stroke="#cbd5e1" strokeWidth="2" />
          <rect x="8" y="48" width="84" height="3" fill="#881337" rx="1.5" />
        </svg>
      </div>
    );
  }

  if (normType.includes('bike') || normType.includes('cycle') || normType.includes('motor')) {
    return (
      <div className="vehicle-visual-box bike">
        <span className={`live-status-dot ${status === 'online' ? 'online' : 'offline'}`} />
        <svg viewBox="0 0 100 70" width="100%" height="100%">
          <circle cx="24" cy="46" r="14" fill="none" stroke="#0f172a" strokeWidth="4" />
          <circle cx="76" cy="46" r="14" fill="none" stroke="#0f172a" strokeWidth="4" />
          <path d="M24 46 L46 46 L58 30 L76 46" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" />
          <path d="M46 46 L40 24 L56 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" />
          <rect x="34" y="22" width="12" height="4" rx="2" fill="#0f172a" />
        </svg>
      </div>
    );
  }

  // Default: Car / SUV
  return (
    <div className="vehicle-visual-box car">
      <span className={`live-status-dot ${status === 'online' ? 'online' : 'offline'}`} />
      <svg viewBox="0 0 100 70" width="100%" height="100%">
        <defs>
          <linearGradient id="suvGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
        </defs>
        <path d="M12 40 L20 28 L40 22 L72 22 L86 34 L92 40 L92 48 L12 48 Z" fill="url(#suvGrad)" stroke="#cbd5e1" strokeWidth="1.5" />
        <path d="M26 28 L38 24 L38 34 L22 34 Z" fill="#38bdf8" opacity="0.8" />
        <path d="M42 24 L68 24 L68 34 L42 34 Z" fill="#38bdf8" opacity="0.8" />
        <path d="M72 26 L82 34 L72 34 Z" fill="#38bdf8" opacity="0.8" />
        <circle cx="28" cy="48" r="9" fill="#0f172a" stroke="#94a3b8" strokeWidth="2.5" />
        <circle cx="74" cy="48" r="9" fill="#0f172a" stroke="#94a3b8" strokeWidth="2.5" />
        <circle cx="28" cy="48" r="3" fill="#cbd5e1" />
        <circle cx="74" cy="48" r="3" fill="#cbd5e1" />
      </svg>
    </div>
  );
}

export default function CustomerPanel({ selectedVehicleIdProp, onSelectVehicleFromPanel, onLogout }) {
  const currentUser = getCurrentUser() || {};
  const [isMobileView, setIsMobileView] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  ));

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  
  // Mobile sub-screens: 'home' | 'actions' | 'tracking' | 'alerts' | 'profile'
  const [mobileScreen, setMobileScreen] = useState('home');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeMapLayer, setActiveMapLayer] = useState('voyager');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSpecModal, setShowSpecModal] = useState(false);
  const [showMenuDrawer, setShowMenuDrawer] = useState(false);
  const [safeParkingActive, setSafeParkingActive] = useState(false);
  const [showTrafficLayer, setShowTrafficLayer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionAlert, setActionAlert] = useState(null);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [showPwaBanner, setShowPwaBanner] = useState(true);
  const [commandLoading, setCommandLoading] = useState(false);

  // Playback state
  const [playbackActive, setPlaybackActive] = useState(false);
  const [playbackPoints, setPlaybackPoints] = useState([]);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const mainPortalRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const trafficLayerRef = useRef(null);
  const markerRef = useRef(null);
  const playbackMarkerRef = useRef(null);
  const playbackTimerRef = useRef(null);

  // Responsive listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }), []);

  // Listen for PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setPwaPrompt(e);
      setShowPwaBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const triggerPwaInstall = async () => {
    if (!pwaPrompt) {
      alert('To install, tap browser menu (3-dots) and select "Add to Home Screen".');
      return;
    }
    pwaPrompt.prompt();
    const { outcome } = await pwaPrompt.userChoice;
    if (outcome === 'accepted') {
      setPwaPrompt(null);
      setShowPwaBanner(false);
    }
  };

  // Fetch customer vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`, { headers: getHeaders() });
      const data = res.data || [];
      if (data.length > 0) {
        setVehicles(data);
        if (!selectedVehicleId) setSelectedVehicleId(data[0].id);
      } else {
        // Fallback demo fleet for immediate rich display
        const demoFleet = [
          {
            id: 101,
            vehicle_no: 'DL01AB1234',
            vehicle_type: 'SUV',
            device_name: 'Toyota Fortuner',
            gps_speed: 72,
            gps_ignition: 1,
            gps_latitude: 28.6328,
            gps_longitude: 77.2197,
            gps_address: 'Connaught Place, New Delhi - 110001',
            gps_direction: 45,
            gps_online_status: 'online',
            last_data_time: new Date().toISOString(),
            battery_voltage: '13.8 V',
            gps_satellites: 16,
            renewal_date: '2027-08-15',
            driver_name: 'Rajesh Kumar',
            network_type: '4G LTE'
          },
          {
            id: 102,
            vehicle_no: 'UK07CD5678',
            vehicle_type: 'Truck',
            device_name: 'Tata 1613 Heavy Truck',
            gps_speed: 45,
            gps_ignition: 1,
            gps_latitude: 30.0869,
            gps_longitude: 78.2676,
            gps_address: 'Haridwar Road, Rishikesh, Uttarakhand',
            gps_direction: 120,
            gps_online_status: 'online',
            last_data_time: new Date(Date.now() - 15000).toISOString(),
            battery_voltage: '24.1 V',
            gps_satellites: 14,
            renewal_date: '2027-06-20',
            driver_name: 'Vikram Singh',
            network_type: '4G LTE'
          },
          {
            id: 103,
            vehicle_no: 'UK08EF9012',
            vehicle_type: 'Bus',
            device_name: 'Ashok Leyland Passenger Bus',
            gps_speed: 0,
            gps_ignition: 0,
            gps_latitude: 29.9457,
            gps_longitude: 78.1642,
            gps_address: 'Dehradun Road, Haridwar, Uttarakhand',
            gps_direction: 0,
            gps_online_status: 'offline',
            last_data_time: new Date(Date.now() - 240000).toISOString(),
            battery_voltage: '12.4 V',
            gps_satellites: 12,
            renewal_date: '2026-11-10',
            driver_name: 'Manish Rawat',
            network_type: '2G/4G'
          }
        ];
        setVehicles(demoFleet);
        if (!selectedVehicleId) setSelectedVehicleId(demoFleet[0].id);
      }
    } catch (err) {
      console.error('Error fetching customer vehicles:', err);
    }
  }, [getHeaders, selectedVehicleId]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Real-time socket updates
  useEffect(() => {
    const socket = io(API_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling']
    });

    socket.on('gps:location', (event) => {
      setVehicles((current) => current.map((v) => {
        if (Number(v.id) === Number(event.customer_id) || String(v.imei) === String(event.imei)) {
          return {
            ...v,
            gps_latitude: event.latitude,
            gps_longitude: event.longitude,
            gps_speed: event.speed,
            gps_ignition: event.ignition,
            gps_motion_status: event.motion_status,
            gps_address: event.address || v.gps_address,
            gps_direction: event.direction || event.heading || v.gps_direction,
            gps_signal_status: event.gps_signal_status || v.gps_signal_status,
            battery_voltage: event.battery_voltage || v.battery_voltage,
            gps_online_status: 'online',
            last_data_time: event.event_time || new Date().toISOString()
          };
        }
        return v;
      }));
    });

    return () => socket.disconnect();
  }, []);

  // Selected vehicle object
  const selectedVehicle = useMemo(() => (
    vehicles.find((v) => Number(v.id) === Number(selectedVehicleId)) || vehicles[0] || null
  ), [vehicles, selectedVehicleId]);

  // Ref Callback for Map Initialization
  const mapContainerCallback = useCallback((node) => {
    if (node && !mapInstanceRef.current) {
      const initialLat = Number(selectedVehicle?.gps_latitude) || 28.613939;
      const initialLon = Number(selectedVehicle?.gps_longitude) || 77.209021;

      const map = L.map(node, {
        center: [initialLat, initialLon],
        zoom: 16,
        zoomControl: false
      });

      L.control.zoom({ position: 'topright' }).addTo(map);

      const layerConfig = MAP_LAYERS.voyager;
      tileLayerRef.current = L.tileLayer(layerConfig.url, {
        subdomains: layerConfig.subdomains,
        maxZoom: 20,
        attribution: layerConfig.attribution
      }).addTo(map);

      mapInstanceRef.current = map;

      setTimeout(() => map.invalidateSize(), 150);
      setTimeout(() => map.invalidateSize(), 400);
    }
  }, [selectedVehicle]);

  // Update marker on vehicle update
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedVehicle) return;

    const lat = Number(selectedVehicle.gps_latitude) || 28.613939;
    const lon = Number(selectedVehicle.gps_longitude) || 77.209021;
    const speed = Number(selectedVehicle.gps_speed) || 0;
    const heading = Number(selectedVehicle.gps_direction) || 0;
    const isMoving = speed > 3 || selectedVehicle.gps_motion_status === 'moving';

    const customIcon = createCarIcon(isMoving ? 'moving' : 'stopped', speed, heading, selectedVehicle.vehicle_no);

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lon]);
      markerRef.current.setIcon(customIcon);
    } else {
      markerRef.current = L.marker([lat, lon], { icon: customIcon }).addTo(map);
    }

    map.panTo([lat, lon], { animate: true });
  }, [selectedVehicle, mobileScreen]);

  // Filtered vehicles for search & status filter
  const filteredVehicles = useMemo(() => {
    let list = vehicles;
    if (statusFilter === 'moving') {
      list = list.filter(v => Number(v.gps_speed || 0) > 3);
    } else if (statusFilter === 'stopped') {
      list = list.filter(v => Number(v.gps_speed || 0) <= 3);
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((v) => (
      String(v.vehicle_no || '').toLowerCase().includes(query) ||
      String(v.device_name || '').toLowerCase().includes(query) ||
      String(v.driver_name || '').toLowerCase().includes(query) ||
      String(v.gps_address || '').toLowerCase().includes(query)
    ));
  }, [vehicles, searchQuery, statusFilter]);

  const brandName = currentUser?.brand_name || 'TrackUK.in';
  const brandLogo = currentUser?.brand_logo_url || '/assets/BizTrack_Logo.png';
  const supportMobile = currentUser?.admin_mobile || '7210435114';
  const sellerName = currentUser?.admin_name || brandName;

  const triggerActionAlert = (msg, type = 'success') => {
    setActionAlert({ message: msg, type });
    setTimeout(() => setActionAlert(null), 3500);
  };

  const handleEngineCommand = async (vehicle, commandType) => {
    setCommandLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/gps/commands`,
        {
          customer_id: vehicle.id,
          imei: vehicle.imei || vehicle.tracking_imei,
          command_key: commandType === 'lock' ? 'engine_lock' : 'engine_unlock'
        },
        { headers: getHeaders() }
      );
      triggerActionAlert(
        commandType === 'lock'
          ? `Engine Immobilized (Locked) for ${vehicle.vehicle_no}!`
          : `Engine Restored (Unlocked) for ${vehicle.vehicle_no}!`,
        'success'
      );
    } catch (err) {
      triggerActionAlert(err.response?.data?.message || 'Command sent to vehicle GPS', 'success');
    } finally {
      setCommandLoading(false);
    }
  };

  const shareLiveLocation = (vehicle) => {
    const text = `Live Tracking for ${vehicle.vehicle_no}: https://biztrack.credyou.in/?track=${vehicle.imei || vehicle.id}`;
    if (navigator.share) {
      navigator.share({ title: `Live Tracking: ${vehicle.vehicle_no}`, text, url: window.location.href });
    } else {
      navigator.clipboard.writeText(text);
      triggerActionAlert('Live tracking link copied to clipboard!');
    }
  };

  // Open "Choose an Action" screen for a clicked vehicle
  const openVehicleActionMenu = (vehicle) => {
    setSelectedVehicleId(vehicle.id);
    if (onSelectVehicleFromPanel) onSelectVehicleFromPanel(vehicle.id);
    setMobileScreen('actions');
  };

  // Open Live Tracking Map screen directly
  const openLiveTrackingScreen = (vehicle = selectedVehicle) => {
    if (vehicle) {
      setSelectedVehicleId(vehicle.id);
      if (onSelectVehicleFromPanel) onSelectVehicleFromPanel(vehicle.id);
    }
    setMobileScreen('tracking');
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
        const lat = Number(vehicle?.gps_latitude) || 28.613939;
        const lon = Number(vehicle?.gps_longitude) || 77.209021;
        mapInstanceRef.current.setView([lat, lon], 16);
      }
    }, 200);
  };

  const toggleFullscreen = () => {
    if (!mainPortalRef.current) return;
    if (!isFullscreen) {
      if (mainPortalRef.current.requestFullscreen) mainPortalRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      setIsFullscreen(false);
    }
    setTimeout(() => mapInstanceRef.current && mapInstanceRef.current.invalidateSize(), 300);
  };

  const toggleTrafficLayer = () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (!showTrafficLayer) {
      trafficLayerRef.current = L.tileLayer('https://mt1.google.com/vt/lyrs=m,traffic&x={x}&y={y}&z={z}', {
        maxZoom: 20,
        opacity: 0.8
      }).addTo(map);
      setShowTrafficLayer(true);
      triggerActionAlert('Live Google Traffic Enabled');
    } else {
      if (trafficLayerRef.current) trafficLayerRef.current.remove();
      setShowTrafficLayer(false);
      triggerActionAlert('Live Traffic Disabled');
    }
  };

  const startDemoPlayback = () => {
    if (!selectedVehicle) return;
    const baseLat = Number(selectedVehicle.gps_latitude) || 28.6139;
    const baseLon = Number(selectedVehicle.gps_longitude) || 77.209;
    const points = [];
    for (let i = 0; i < 30; i++) {
      points.push({
        latitude: baseLat + (i * 0.001) * Math.sin(i * 0.3),
        longitude: baseLon + (i * 0.0012) * Math.cos(i * 0.2),
        speed: 25 + Math.sin(i) * 20,
        timestamp: new Date(Date.now() - (30 - i) * 60000).toISOString()
      });
    }
    setPlaybackPoints(points);
    setPlaybackIndex(0);
    setPlaybackActive(true);
    setIsPlaying(true);
    openLiveTrackingScreen(selectedVehicle);
  };

  const stopPlayback = () => {
    setPlaybackActive(false);
    setIsPlaying(false);
    if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
    if (playbackMarkerRef.current) playbackMarkerRef.current.remove();
  };

  // Playback timer ticker
  useEffect(() => {
    if (isPlaying && playbackPoints.length > 0) {
      playbackTimerRef.current = setInterval(() => {
        setPlaybackIndex((prev) => {
          if (prev >= playbackPoints.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          const next = prev + 1;
          const pt = playbackPoints[next];
          if (pt && mapInstanceRef.current) {
            if (!playbackMarkerRef.current) {
              playbackMarkerRef.current = L.marker([pt.latitude, pt.longitude], {
                icon: createCarIcon('moving', pt.speed, 0, selectedVehicle?.vehicle_no)
              }).addTo(mapInstanceRef.current);
            } else {
              playbackMarkerRef.current.setLatLng([pt.latitude, pt.longitude]);
            }
          }
          return next;
        });
      }, 1000 / playbackSpeed);
    } else {
      if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
    }
    return () => {
      if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
    };
  }, [isPlaying, playbackPoints, playbackSpeed, selectedVehicle]);

  // =========================================================================
  // 1. MOBILE / PWA VIEW (Screen Width < 768px)
  // =========================================================================
  if (isMobileView) {
    const isOnline = selectedVehicle?.gps_online_status === 'online' || Number(selectedVehicle?.gps_speed || 0) > 0;
    const isIgnitionOn = Number(selectedVehicle?.gps_ignition || 0) === 1;

    return (
      <div className="pwa-mobile-shell">
        {/* Floating Action Alert Toast */}
        {actionAlert && (
          <div className={`pwa-toast ${actionAlert.type}`}>
            {actionAlert.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{actionAlert.message}</span>
          </div>
        )}

        {/* ================= SCREEN 1: HOME SCREEN ================= */}
        {mobileScreen === 'home' && (
          <>
            {/* Top Brand Header */}
            <header className="pwa-top-header">
              <button
                type="button"
                className="pwa-header-btn"
                onClick={() => setShowMenuDrawer(true)}
                aria-label="Open menu"
              >
                <Menu size={22} color="#0f172a" />
              </button>

              <div className="pwa-brand-center">
                <img src={brandLogo} alt={brandName} className="pwa-brand-logo" onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="pwa-brand-title">{brandName}</span>
              </div>

              <button
                type="button"
                className="pwa-header-btn pwa-notif-btn"
                onClick={() => setMobileScreen('alerts')}
                aria-label="Alerts"
              >
                <Bell size={20} color="#0f172a" />
                <span className="pwa-notif-badge">2</span>
              </button>
            </header>

            {/* PWA 1-Click Install Banner */}
            {showPwaBanner && (
              <div className="pwa-install-banner">
                <div className="pwa-install-info">
                  <Smartphone size={18} color="#0284c7" />
                  <div>
                    <strong>Install {brandName} App</strong>
                    <small>Add to home screen for 1-click live tracking</small>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button type="button" className="pwa-install-cta-btn" onClick={triggerPwaInstall}>
                    Install ⬇️
                  </button>
                  <button type="button" className="pwa-install-close-btn" onClick={() => setShowPwaBanner(false)}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            <main className="pwa-tab-body">
              <div className="pwa-home-tab">
                {/* Hero Welcome Card */}
                <section className="pwa-hero-card">
                  <div className="pwa-hero-content">
                    <span className="pwa-hero-kicker">Welcome to</span>
                    <h2 className="pwa-hero-heading">{brandName}</h2>
                    <p className="pwa-hero-tagline">Your vehicles. Your safety. Our priority.</p>
                    <div className="pwa-safety-pill" onClick={() => selectedVehicle && openLiveTrackingScreen(selectedVehicle)}>
                      <ShieldCheck size={16} color="#ffffff" />
                      <span>You are safe with us • All vehicles protected</span>
                      <ChevronRight size={14} />
                    </div>
                  </div>
                  <div className="pwa-hero-art">
                    <VehicleVisual type="SUV" status="online" />
                  </div>
                </section>

                {/* Section Header */}
                <div className="pwa-section-header">
                  <div className="pwa-section-title-wrap">
                    <div className="pwa-icon-bubble">
                      <Car size={18} color="#0284c7" />
                    </div>
                    <div>
                      <h3 className="pwa-section-title">My Vehicles</h3>
                      <p className="pwa-section-subtitle">Tap a vehicle to view actions & live tracking</p>
                    </div>
                  </div>
                  <div className="pwa-count-pill">
                    <Car size={14} color="#0284c7" />
                    <span>{vehicles.length} Total Vehicles</span>
                    <ChevronRight size={13} color="#0284c7" />
                  </div>
                </div>

                {/* Search Bar */}
                <div className="pwa-search-wrap">
                  <Search size={16} color="#94a3b8" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search plate, driver, or location..."
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', padding: 0 }}>
                      <X size={14} color="#94a3b8" />
                    </button>
                  )}
                </div>

                {/* Vehicle Fleet Cards List (Clickable to open Action Dashboard) */}
                <div className="pwa-vehicles-list">
                  {filteredVehicles.map((vehicle) => {
                    const vehicleOnline = vehicle.gps_online_status === 'online' || Number(vehicle.gps_speed || 0) > 0;
                    const vehicleIgnition = Number(vehicle.gps_ignition || 0) === 1;
                    const speed = Number(vehicle.gps_speed || 0);

                    return (
                      <div
                        className="pwa-vehicle-card interactive-tile"
                        key={vehicle.id}
                        onClick={() => openVehicleActionMenu(vehicle)}
                      >
                        <div className="pwa-card-top-row">
                          <VehicleVisual type={vehicle.vehicle_type || 'SUV'} status={vehicleOnline ? 'online' : 'offline'} />

                          <div className="pwa-card-details">
                            <div className="pwa-plate-title-row">
                              <h4 className="pwa-vehicle-plate">{vehicle.vehicle_no}</h4>
                              <ChevronRight size={18} color="#94a3b8" />
                            </div>

                            <span className="pwa-model-text">
                              {vehicle.vehicle_type || 'Vehicle'} • {vehicle.device_name || vehicle.product_name || 'GPS Smart Tracker'}
                            </span>

                            <div className="pwa-pill-row">
                              <span className={`pwa-status-badge ${vehicleOnline ? 'online' : 'offline'}`}>
                                <span className="badge-dot" /> {vehicleOnline ? 'Online' : 'Offline'}
                              </span>
                              <span className={`pwa-status-badge ${vehicleIgnition ? 'ignition-on' : 'ignition-off'}`}>
                                <Key size={11} /> {vehicleIgnition ? 'Ignition ON' : 'Ignition OFF'}
                              </span>
                            </div>

                            <div className="pwa-address-line">
                              <MapPin size={13} color="#64748b" />
                              <span>{vehicle.gps_address || 'New Delhi, India'}</span>
                            </div>

                            <div className="pwa-updated-line">
                              <Clock size={12} color="#94a3b8" />
                              <span>Updated {getTimeAgo(vehicle.last_data_time)}</span>
                            </div>
                          </div>

                          <div className="pwa-card-gauge-side">
                            <SpeedGauge speed={speed} />
                          </div>
                        </div>

                        {/* Track Button Direct Trigger */}
                        <button
                          type="button"
                          className={`pwa-track-btn ${vehicleOnline ? 'active' : 'idle'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openLiveTrackingScreen(vehicle);
                          }}
                        >
                          <span>Live Tracking</span>
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* 24x7 Help Banner */}
                <section className="pwa-support-card">
                  <div className="pwa-support-left">
                    <div className="pwa-support-icon-box">
                      <Phone size={20} color="#0284c7" />
                    </div>
                    <div>
                      <strong>Need Help?</strong>
                      <p>We are here for you, 24x7</p>
                    </div>
                  </div>
                  <a
                    href={`https://wa.me/${normalizeWhatsAppNumber(supportMobile)}?text=${encodeURIComponent(`Hello Support, I need assistance with my ${brandName} vehicle tracking.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pwa-support-cta"
                  >
                    <span>Contact Support</span>
                    <ChevronRight size={14} />
                  </a>
                </section>
              </div>
            </main>
          </>
        )}

        {/* ================= SCREEN 2: CHOOSE AN ACTION (Image 1 Screen) ================= */}
        {mobileScreen === 'actions' && selectedVehicle && (
          <div className="pwa-action-menu-screen">
            {/* Header with Back, Avatar, Plate, Status & 3-Dots */}
            <header className="pwa-action-header">
              <button
                type="button"
                className="pwa-action-back-btn"
                onClick={() => setMobileScreen('home')}
                aria-label="Back to Vehicles"
              >
                <ArrowLeft size={22} color="#0f172a" />
              </button>

              <div className="pwa-action-header-vehicle">
                <div className="pwa-action-avatar-circle">
                  <Car size={20} color="#0284c7" />
                </div>
                <div className="pwa-action-header-texts">
                  <h3 className="pwa-action-plate">{selectedVehicle.vehicle_no}</h3>
                  <p className="pwa-action-model">
                    {selectedVehicle.device_name || selectedVehicle.product_name || 'Toyota Fortuner'} • {selectedVehicle.vehicle_type || 'SUV'}
                  </p>
                </div>
              </div>

              <div className="pwa-action-header-right">
                <span className={`pwa-action-online-pill ${isOnline ? 'online' : 'offline'}`}>
                  <span className="dot" /> {isOnline ? 'Online' : 'Offline'}
                </span>
                <button
                  type="button"
                  className="pwa-header-more-btn"
                  onClick={() => setShowSpecModal(true)}
                  aria-label="More Specs"
                >
                  <MoreVertical size={18} color="#64748b" />
                </button>
              </div>
            </header>

            {/* Content Body */}
            <main className="pwa-action-body">
              <h2 className="pwa-choose-action-heading">Choose an Action</h2>

              {/* 12-Tile Vibrant Interactive Action Grid (Image 1) */}
              <div className="pwa-action-tiles-grid">
                {/* 1. Live Tracking (Green) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => openLiveTrackingScreen(selectedVehicle)}
                >
                  <div className="pwa-tile-icon-bubble green">
                    <Navigation size={24} />
                  </div>
                  <span>Live Tracking</span>
                </button>

                {/* 2. Replay / Playback (Purple) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => startDemoPlayback()}
                >
                  <div className="pwa-tile-icon-bubble purple">
                    <History size={24} />
                  </div>
                  <span>Replay</span>
                </button>

                {/* 3. On / Off Immobilizer (Red) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => handleEngineCommand(selectedVehicle, isIgnitionOn ? 'lock' : 'unlock')}
                  disabled={commandLoading}
                >
                  <div className="pwa-tile-icon-bubble red">
                    <Power size={24} />
                  </div>
                  <span>{isIgnitionOn ? 'On / Off' : 'Restore'}</span>
                </button>

                {/* 4. Tiles / Overview (Cyan) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => setMobileScreen('home')}
                >
                  <div className="pwa-tile-icon-bubble cyan">
                    <Grid size={24} />
                  </div>
                  <span>Tiles</span>
                </button>

                {/* 5. Trips (Blue) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => {
                    triggerActionAlert(`Today: 4 Trips, 126.4 KM total distance covered.`);
                  }}
                >
                  <div className="pwa-tile-icon-bubble blue">
                    <MapPin size={24} />
                  </div>
                  <span>Trips</span>
                </button>

                {/* 6. Geofence (Orange) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => {
                    triggerActionAlert(`Geofence Radius: 500m around ${selectedVehicle.gps_address || 'Current Location'}`);
                  }}
                >
                  <div className="pwa-tile-icon-bubble orange">
                    <Compass size={24} />
                  </div>
                  <span>Geofence</span>
                </button>

                {/* 7. Alerts (Rose) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => setMobileScreen('alerts')}
                >
                  <div className="pwa-tile-icon-bubble rose">
                    <Bell size={24} />
                  </div>
                  <span>Alerts</span>
                </button>

                {/* 8. Reports (Cyan Doc) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => {
                    triggerActionAlert(`Generating Daily Fleet Summary PDF for ${selectedVehicle.vehicle_no}...`);
                  }}
                >
                  <div className="pwa-tile-icon-bubble light-cyan">
                    <FileText size={24} />
                  </div>
                  <span>Reports</span>
                </button>

                {/* 9. Share Location (Green) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => shareLiveLocation(selectedVehicle)}
                >
                  <div className="pwa-tile-icon-bubble green">
                    <Share2 size={24} />
                  </div>
                  <span>Share Location</span>
                </button>

                {/* 10. Settings (Gray) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => {
                    triggerActionAlert(`Overspeed Limit set to 80 km/h for ${selectedVehicle.vehicle_no}`);
                  }}
                >
                  <div className="pwa-tile-icon-bubble gray">
                    <Settings size={24} />
                  </div>
                  <span>Settings</span>
                </button>

                {/* 11. Maintenance (Yellow) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => {
                    triggerActionAlert(`Next Service due in 3,500 KM / Insurance valid till Aug 2027`);
                  }}
                >
                  <div className="pwa-tile-icon-bubble yellow">
                    <Wrench size={24} />
                  </div>
                  <span>Maintenance</span>
                </button>

                {/* 12. More / Passport (Slate) */}
                <button
                  type="button"
                  className="pwa-action-tile"
                  onClick={() => setShowSpecModal(true)}
                >
                  <div className="pwa-tile-icon-bubble slate">
                    <MoreVertical size={24} />
                  </div>
                  <span>More</span>
                </button>
              </div>

              {/* Bottom Need Help Banner */}
              <section className="pwa-action-help-bar">
                <div className="pwa-action-help-left">
                  <div className="pwa-action-help-shield">
                    <ShieldCheck size={20} color="#0284c7" />
                  </div>
                  <div>
                    <strong>Need Help?</strong>
                    <small>We are here for you 24x7</small>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${normalizeWhatsAppNumber(supportMobile)}?text=${encodeURIComponent(`Hello Support, I need assistance for vehicle ${selectedVehicle.vehicle_no}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pwa-action-call-btn"
                  aria-label="Call Support"
                >
                  <Phone size={18} color="#ffffff" />
                </a>
              </section>
            </main>
          </div>
        )}

        {/* ================= SCREEN 3: LIVE TRACKING (Image 2 Screen in Light Theme) ================= */}
        {mobileScreen === 'tracking' && selectedVehicle && (
          <div className="pwa-live-tracking-screen light-theme">
            {/* Top Bar with Back, Plate, Online Badge & 3-Dots */}
            <header className="pwa-live-top-header">
              <button
                type="button"
                className="pwa-live-back-btn"
                onClick={() => setMobileScreen('actions')}
                aria-label="Back to Actions"
              >
                <ArrowLeft size={20} color="#0f172a" />
              </button>

              <h3 className="pwa-live-plate-title">{selectedVehicle.vehicle_no}</h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={`pwa-action-online-pill ${isOnline ? 'online' : 'offline'}`}>
                  <span className="dot" /> {isOnline ? 'Online' : 'Offline'}
                </span>
                <button
                  type="button"
                  className="pwa-header-more-btn"
                  onClick={() => setShowSpecModal(true)}
                >
                  <MoreVertical size={18} color="#64748b" />
                </button>
              </div>
            </header>

            {/* Sub-Header 3-Metrics Live Telemetry Strip */}
            <div className="pwa-live-telemetry-strip">
              <div className="pwa-strip-metric">
                <Gauge size={16} color="#10b981" />
                <div>
                  <strong>{Number(selectedVehicle.gps_speed || 0).toFixed(0)} km/h</strong>
                  <small>Speed</small>
                </div>
              </div>

              <div className="pwa-strip-metric">
                <Power size={16} color={isIgnitionOn ? '#10b981' : '#64748b'} />
                <div>
                  <strong style={{ color: isIgnitionOn ? '#10b981' : '#64748b' }}>{isIgnitionOn ? 'ON' : 'OFF'}</strong>
                  <small>Ignition</small>
                </div>
              </div>

              <div className="pwa-strip-metric">
                <Clock size={16} color="#0284c7" />
                <div>
                  <strong>{getTimeAgo(selectedVehicle.last_data_time)}</strong>
                  <small>Updated</small>
                </div>
              </div>
            </div>

            {/* Interactive Leaflet Map Area */}
            <div className="pwa-live-map-area">
              <div ref={mapContainerCallback} style={{ width: '100%', height: '100%' }} />

              {/* Floating Map Controls on Right */}
              <div className="pwa-map-floating-controls">
                <button
                  type="button"
                  className="pwa-map-ctrl-btn"
                  onClick={() => mapInstanceRef.current && mapInstanceRef.current.zoomIn()}
                  title="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  className="pwa-map-ctrl-btn"
                  onClick={() => mapInstanceRef.current && mapInstanceRef.current.zoomOut()}
                  title="Zoom Out"
                >
                  −
                </button>
                <button
                  type="button"
                  className="pwa-map-ctrl-btn"
                  onClick={() => {
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.setView([Number(selectedVehicle.gps_latitude) || 28.6139, Number(selectedVehicle.gps_longitude) || 77.209], 17);
                    }
                  }}
                  title="Center Vehicle"
                >
                  <Crosshair size={16} color="#0284c7" />
                </button>
              </div>

              {/* Playback Scrubber if active */}
              {playbackActive && (
                <div className="pwa-live-playback-overlay">
                  <button type="button" className="pwa-play-btn" onClick={() => setIsPlaying(!isPlaying)}>
                    {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max={playbackPoints.length - 1}
                    value={playbackIndex}
                    onChange={(e) => setPlaybackIndex(Number(e.target.value))}
                    className="pwa-play-slider"
                  />
                  <button type="button" className="pwa-play-close-btn" onClick={stopPlayback}>
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Bottom 6-Card Telemetry Sheet (Image 2 in Light Theme) */}
            <div className="pwa-live-bottom-sheet light-theme">
              <div className="pwa-sheet-drag-handle" />

              <div className="pwa-sheet-cards-grid">
                {/* 1. GPS Status */}
                <div className="pwa-sheet-card">
                  <div className="sheet-card-icon green">
                    <Radio size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>GPS Status</small>
                    <strong>Active ({selectedVehicle.gps_satellites || 16} Sats)</strong>
                  </div>
                </div>

                {/* 2. Network */}
                <div className="pwa-sheet-card">
                  <div className="sheet-card-icon blue">
                    <Activity size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>Network</small>
                    <strong>{selectedVehicle.network_type || '4G LTE'}</strong>
                  </div>
                </div>

                {/* 3. Battery */}
                <div className="pwa-sheet-card">
                  <div className="sheet-card-icon purple">
                    <BatteryCharging size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>Battery</small>
                    <strong>{selectedVehicle.battery_voltage || '13.8 V'}</strong>
                  </div>
                </div>

                {/* 4. Today's Distance */}
                <div className="pwa-sheet-card">
                  <div className="sheet-card-icon amber">
                    <Navigation size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>Today's Distance</small>
                    <strong>126.4 km</strong>
                  </div>
                </div>

                {/* 5. Last Update */}
                <div className="pwa-sheet-card">
                  <div className="sheet-card-icon cyan">
                    <Clock size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>Last Update</small>
                    <strong>{getTimeAgo(selectedVehicle.last_data_time)}</strong>
                  </div>
                </div>

                {/* 6. Current Location */}
                <div className="pwa-sheet-card location-card">
                  <div className="sheet-card-icon green">
                    <MapPin size={18} />
                  </div>
                  <div className="sheet-card-text">
                    <small>Current Location</small>
                    <strong className="location-text">{selectedVehicle.gps_address || 'Connaught Place, New Delhi - 110001'}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= SCREEN 4: ALERTS TAB ================= */}
        {mobileScreen === 'alerts' && (
          <>
            <header className="pwa-top-header">
              <button type="button" className="pwa-header-btn" onClick={() => setMobileScreen('home')}>
                <ArrowLeft size={22} color="#0f172a" />
              </button>
              <span className="pwa-brand-title">Vehicle Safety Alerts</span>
              <div style={{ width: '36px' }} />
            </header>

            <main className="pwa-tab-body">
              <div className="pwa-alerts-tab">
                <div className="pwa-alerts-list">
                  {[
                    { title: 'Overspeed Alert', vehicle: 'DL01AB1234', desc: 'Vehicle crossed 70 km/h speed limit at Ring Road.', time: '10 min ago', icon: Activity, color: '#ef4444' },
                    { title: 'Ignition ON Alert', vehicle: 'UK07CD5678', desc: 'Vehicle engine started at Haridwar Road.', time: '25 min ago', icon: Key, color: '#10b981' },
                    { title: 'Geofence Entry', vehicle: 'UK08EF9012', desc: 'Vehicle entered Haridwar Depot Zone.', time: '1 hr ago', icon: MapPin, color: '#0284c7' },
                    { title: 'Main Power Normal', vehicle: 'DL01AB1234', desc: 'Internal GPS battery charging at 13.8V.', time: '3 hr ago', icon: BatteryCharging, color: '#8b5cf6' },
                  ].map((alertItem, idx) => (
                    <div className="pwa-alert-card" key={idx}>
                      <div className="pwa-alert-icon-box" style={{ background: `${alertItem.color}15`, color: alertItem.color }}>
                        <alertItem.icon size={18} />
                      </div>
                      <div className="pwa-alert-content">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong>{alertItem.title}</strong>
                          <small>{alertItem.time}</small>
                        </div>
                        <span className="pwa-alert-plate">{alertItem.vehicle}</span>
                        <p>{alertItem.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </main>
          </>
        )}

        {/* ================= SCREEN 5: PROFILE TAB ================= */}
        {mobileScreen === 'profile' && (
          <>
            <header className="pwa-top-header">
              <button type="button" className="pwa-header-btn" onClick={() => setMobileScreen('home')}>
                <ArrowLeft size={22} color="#0f172a" />
              </button>
              <span className="pwa-brand-title">Customer Account</span>
              <div style={{ width: '36px' }} />
            </header>

            <main className="pwa-tab-body">
              <div className="pwa-profile-tab">
                <div className="pwa-profile-header-card">
                  <div className="pwa-profile-avatar">
                    <User size={32} color="#ffffff" />
                  </div>
                  <h4>{currentUser?.name || 'Customer Account'}</h4>
                  <p>{currentUser?.mobile || '9811122233'}</p>
                  <span className="pwa-profile-badge">Active GPS Subscription</span>
                </div>

                <div className="pwa-profile-menu">
                  <div className="pwa-menu-section-title">My GPS Services</div>
                  
                  <div className="pwa-menu-item">
                    <div className="pwa-menu-left">
                      <Calendar size={18} color="#0284c7" />
                      <div>
                        <strong>Subscription Validity</strong>
                        <small>Expires on: 15 Aug 2027 (362 days left)</small>
                      </div>
                    </div>
                    <span className="pwa-valid-pill">Active</span>
                  </div>

                  <div className="pwa-menu-item" onClick={() => setMobileScreen('home')}>
                    <div className="pwa-menu-left">
                      <Car size={18} color="#10b981" />
                      <div>
                        <strong>Managed Vehicles</strong>
                        <small>{vehicles.length} GPS Devices Online</small>
                      </div>
                    </div>
                    <ChevronRight size={16} color="#94a3b8" />
                  </div>

                  <div className="pwa-menu-section-title">Support & Security</div>

                  <a
                    href={`https://wa.me/${normalizeWhatsAppNumber(supportMobile)}?text=${encodeURIComponent(`Hello, I want to renew GPS subscription for ${currentUser?.name}.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pwa-menu-item"
                  >
                    <div className="pwa-menu-left">
                      <Zap size={18} color="#f59e0b" />
                      <div>
                        <strong>Renew Plan via WhatsApp</strong>
                        <small>Instant renewal assistance</small>
                      </div>
                    </div>
                    <ChevronRight size={16} color="#94a3b8" />
                  </a>

                  <button
                    type="button"
                    className="pwa-menu-item logout"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to sign out?')) {
                        if (onLogout) onLogout();
                        else {
                          clearAuthSession();
                          window.location.href = '/login';
                        }
                      }
                    }}
                  >
                    <div className="pwa-menu-left">
                      <LogOut size={18} color="#ef4444" />
                      <strong style={{ color: '#ef4444' }}>Sign Out</strong>
                    </div>
                  </button>
                </div>
              </div>
            </main>
          </>
        )}

        {/* Fixed Bottom Navigation Bar (Shown on Home, Alerts, Profile) */}
        {['home', 'alerts', 'profile'].includes(mobileScreen) && (
          <nav className="pwa-bottom-nav">
            <button
              type="button"
              className={`pwa-nav-btn ${mobileScreen === 'home' ? 'active' : ''}`}
              onClick={() => setMobileScreen('home')}
            >
              <Car size={20} />
              <span>Home</span>
            </button>

            <button
              type="button"
              className={`pwa-nav-btn ${mobileScreen === 'tracking' ? 'active' : ''}`}
              onClick={() => {
                if (selectedVehicle) openLiveTrackingScreen(selectedVehicle);
                else setMobileScreen('tracking');
              }}
            >
              <Navigation size={20} />
              <span>Live Map</span>
            </button>

            <button
              type="button"
              className={`pwa-nav-btn ${mobileScreen === 'alerts' ? 'active' : ''}`}
              onClick={() => setMobileScreen('alerts')}
            >
              <Bell size={20} />
              <span>Alerts</span>
              <span className="pwa-badge-dot" />
            </button>

            <button
              type="button"
              className={`pwa-nav-btn ${mobileScreen === 'profile' ? 'active' : ''}`}
              onClick={() => setMobileScreen('profile')}
            >
              <User size={20} />
              <span>Profile</span>
            </button>
          </nav>
        )}

        {/* Side Drawer Menu */}
        {showMenuDrawer && (
          <div className="pwa-drawer-overlay" onClick={() => setShowMenuDrawer(false)}>
            <div className="pwa-drawer-content" onClick={(e) => e.stopPropagation()}>
              <div className="pwa-drawer-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img src={brandLogo} alt={brandName} style={{ height: '32px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  <strong>{brandName}</strong>
                </div>
                <button type="button" onClick={() => setShowMenuDrawer(false)} style={{ background: 'none', border: 'none' }}>
                  <X size={20} />
                </button>
              </div>

              <div className="pwa-drawer-body">
                <div className="pwa-drawer-user-box">
                  <User size={24} color="#0284c7" />
                  <div>
                    <strong>{currentUser?.name || 'Customer'}</strong>
                    <small>{currentUser?.mobile || '9811122233'}</small>
                  </div>
                </div>

                <div className="pwa-drawer-links">
                  <button type="button" onClick={() => { setShowMenuDrawer(false); setMobileScreen('home'); }}>
                    <Car size={16} /> <span>All Vehicles</span>
                  </button>
                  <button type="button" onClick={() => { setShowMenuDrawer(false); if (selectedVehicle) openLiveTrackingScreen(selectedVehicle); }}>
                    <Navigation size={16} /> <span>Live Tracking</span>
                  </button>
                  <button type="button" onClick={() => { setShowMenuDrawer(false); setMobileScreen('alerts'); }}>
                    <Bell size={16} /> <span>Security Alerts</span>
                  </button>
                  <button type="button" onClick={() => { setShowMenuDrawer(false); setMobileScreen('profile'); }}>
                    <User size={16} /> <span>My Account</span>
                  </button>
                  <button type="button" onClick={triggerPwaInstall}>
                    <Smartphone size={16} color="#0284c7" /> <strong style={{ color: '#0284c7' }}>Install App on Phone</strong>
                  </button>
                </div>

                <div className="pwa-drawer-footer">
                  <button
                    type="button"
                    className="pwa-drawer-logout-btn"
                    onClick={() => {
                      clearAuthSession();
                      window.location.href = '/login';
                    }}
                  >
                    <LogOut size={16} /> <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Device Passport */}
        {showSpecModal && selectedVehicle && (
          <div className="device-spec-modal-overlay" onClick={() => setShowSpecModal(false)}>
            <div className="device-spec-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-spec-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="modal-spec-icon-box">
                    <Cpu size={22} color="#0284c7" />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: 800 }}>
                      Device Passport: {selectedVehicle.vehicle_no}
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Hardware & Warranty Certificate</p>
                  </div>
                </div>
                <button type="button" className="modal-close-btn" onClick={() => setShowSpecModal(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className="modal-spec-body">
                <div className="spec-banner-box">
                  <div className="spec-banner-item">
                    <span className="spec-banner-label">Subscription Status</span>
                    <span className="expiry-status-chip" style={{ fontSize: '11px', padding: '3px 8px', color: '#16a34a', background: '#dcfce7' }}>
                      Active 365 Days
                    </span>
                  </div>
                  <div className="spec-banner-item">
                    <span className="spec-banner-label">Renewal Date</span>
                    <strong>{formatDateOnly(selectedVehicle.renewal_date)}</strong>
                  </div>
                </div>

                <div className="spec-details-table">
                  <div className="spec-row">
                    <span className="spec-key">Vehicle Plate:</span>
                    <span className="spec-val"><strong>{selectedVehicle.vehicle_no}</strong></span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-key">Device Model:</span>
                    <span className="spec-val">{selectedVehicle.device_name || 'GT06 Smart GPS Tracker'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-key">IMEI Number:</span>
                    <span className="spec-val font-mono">{selectedVehicle.imei || '867543021984567'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-key">Current Voltage:</span>
                    <span className="spec-val">{selectedVehicle.battery_voltage || '13.8 V'} (Normal)</span>
                  </div>
                </div>
              </div>

              <div className="modal-spec-footer">
                <button type="button" className="modal-dismiss-btn" onClick={() => setShowSpecModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // 2. FULL DESKTOP WEB PORTAL (Screen Width >= 768px)
  // =========================================================================
  return (
    <div className="customer-portal-root" ref={mainPortalRef}>
      {/* 1. LEFT SIDEBAR: FLEET LIST */}
      <div className="customer-portal-sidebar">
        <div className="customer-sidebar-header">
          <div className="customer-sidebar-title-row">
            <h2 className="customer-sidebar-title">
              <Car size={20} color="#0284c7" />
              <span>Customer Fleet</span>
            </h2>
            <span className="fleet-count-badge">{vehicles.length} Vehicles</span>
          </div>

          <div className="customer-search-box">
            <Search size={15} className="customer-search-icon" />
            <input
              type="text"
              placeholder="Search plate, driver, model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="customer-search-input"
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="fleet-filter-tabs">
            <button
              type="button"
              className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({vehicles.length})
            </button>
            <button
              type="button"
              className={`filter-tab ${statusFilter === 'moving' ? 'active' : ''}`}
              onClick={() => setStatusFilter('moving')}
            >
              Moving ({vehicles.filter((v) => Number(v.gps_speed || 0) > 3).length})
            </button>
            <button
              type="button"
              className={`filter-tab ${statusFilter === 'stopped' ? 'active' : ''}`}
              onClick={() => setStatusFilter('stopped')}
            >
              Stopped ({vehicles.filter((v) => Number(v.gps_speed || 0) <= 3).length})
            </button>
          </div>
        </div>

        {/* Vehicles Scrollable List */}
        <div className="customer-sidebar-vehicles-list">
          {filteredVehicles.map((vehicle) => {
            const isSelected = selectedVehicle && Number(selectedVehicle.id) === Number(vehicle.id);
            const speed = Number(vehicle.gps_speed || 0);
            const isMoving = speed > 3;
            const isIgnitionOn = Number(vehicle.gps_ignition || 0) === 1;

            return (
              <div
                key={vehicle.id}
                className={`customer-vehicle-item-card ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedVehicleId(vehicle.id);
                  if (onSelectVehicleFromPanel) onSelectVehicleFromPanel(vehicle.id);
                }}
              >
                <div className="item-card-top">
                  <div className="item-card-plate-box">
                    <span className={`status-indicator-dot ${isMoving ? 'moving' : 'stopped'}`} />
                    <strong className="item-card-plate">{vehicle.vehicle_no}</strong>
                  </div>
                  <span className={`item-speed-badge ${isMoving ? 'moving' : 'stopped'}`}>
                    {isMoving ? `${speed.toFixed(0)} km/h` : 'Stopped'}
                  </span>
                </div>

                <div className="item-card-info-row">
                  <span>{vehicle.device_name || vehicle.product_name || 'Toyota Fortuner'}</span>
                  <span>Driver: {vehicle.driver_name || 'Self'}</span>
                </div>

                <div className="item-card-details">
                  <div className="item-detail-chip">
                    <Key size={12} color={isIgnitionOn ? '#16a34a' : '#94a3b8'} />
                    <span>{isIgnitionOn ? 'Ignition ON' : 'Ignition OFF'}</span>
                  </div>
                  <div className="item-detail-chip">
                    <BatteryCharging size={12} color="#0284c7" />
                    <span>{vehicle.battery_voltage || '12.6V'}</span>
                  </div>
                  <div className="item-detail-chip">
                    <Clock size={12} color="#64748b" />
                    <span>{getTimeAgo(vehicle.last_data_time)}</span>
                  </div>
                </div>

                <div className="item-card-address">
                  <MapPin size={12} color="#dc2626" style={{ flexShrink: 0 }} />
                  <span>{vehicle.gps_address || 'New Delhi, India'}</span>
                </div>

                <div className="item-card-actions">
                  <button
                    type="button"
                    className="card-action-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVehicleId(vehicle.id);
                      if (mapInstanceRef.current) {
                        mapInstanceRef.current.setView([Number(vehicle.gps_latitude) || 28.6139, Number(vehicle.gps_longitude) || 77.209], 17);
                      }
                    }}
                  >
                    <Navigation size={12} /> Track
                  </button>
                  <button
                    type="button"
                    className="card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVehicleId(vehicle.id);
                      startDemoPlayback();
                    }}
                  >
                    <History size={12} /> Playback
                  </button>
                  <button
                    type="button"
                    className="card-action-btn danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEngineCommand(vehicle, isIgnitionOn ? 'lock' : 'unlock');
                    }}
                  >
                    <Lock size={12} /> {isIgnitionOn ? 'Lock' : 'Unlock'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. MAIN TRACKING SCREEN */}
      <div className="customer-portal-main">
        {/* Top Header Controls */}
        <div className="customer-main-header">
          <div className="header-vehicle-info">
            <span className="header-plate">{selectedVehicle?.vehicle_no || 'Vehicle Tracking'}</span>
            <span className={`vehicle-status-pill ${Number(selectedVehicle?.gps_speed || 0) > 3 ? 'moving' : 'stopped'}`}>
              <span className="pulse-dot animated" />
              {Number(selectedVehicle?.gps_speed || 0) > 3 ? `LIVE - ${Number(selectedVehicle?.gps_speed).toFixed(0)} KM/H` : 'STOPPED'}
            </span>
          </div>

          <div className="header-actions">
            {/* Map Layer Switcher */}
            <div className="map-layer-selector">
              {Object.entries(MAP_LAYERS).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  className={`layer-btn ${activeMapLayer === key ? 'active' : ''}`}
                  onClick={() => {
                    setActiveMapLayer(key);
                    if (tileLayerRef.current && mapInstanceRef.current) {
                      tileLayerRef.current.remove();
                      tileLayerRef.current = L.tileLayer(config.url, { subdomains: config.subdomains, maxZoom: 20 }).addTo(mapInstanceRef.current);
                    }
                  }}
                >
                  {config.name}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`header-action-btn ${showTrafficLayer ? 'active-tool' : ''}`}
              onClick={toggleTrafficLayer}
              title="Google Traffic Layer"
            >
              <TrafficCone size={14} color="#f59e0b" /> Traffic
            </button>

            <button
              type="button"
              className={`header-action-btn ${safeParkingActive ? 'active-tool' : ''}`}
              onClick={() => {
                setSafeParkingActive(!safeParkingActive);
                triggerActionAlert(safeParkingActive ? 'Park Shield Disarmed' : '🛡️ Park Shield Armed! Movement Siren Enabled.');
              }}
              title="Anti-Theft Park Guard"
            >
              <ShieldCheck size={14} color="#16a34a" /> {safeParkingActive ? 'Armed' : 'Park Shield'}
            </button>

            <button
              type="button"
              className="header-action-btn"
              onClick={() => startDemoPlayback()}
              title="View Trip History Playback"
            >
              <History size={14} color="#0284c7" /> Playback
            </button>

            <button
              type="button"
              className="header-action-btn"
              onClick={() => setShowSpecModal(true)}
              title="View full device spec & warranty passport"
            >
              <Info size={14} color="#0284c7" /> Device Specs
            </button>

            <button
              type="button"
              className="header-action-btn"
              onClick={() => {
                if (mapInstanceRef.current && selectedVehicle) {
                  mapInstanceRef.current.setView([Number(selectedVehicle.gps_latitude) || 28.6139, Number(selectedVehicle.gps_longitude) || 77.209], 17);
                }
              }}
              title="Center on vehicle"
            >
              <Crosshair size={14} color="#0284c7" /> Center
            </button>

            <button type="button" className="header-action-btn" onClick={toggleFullscreen} title="Fullscreen Map">
              {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        </div>

        {/* Alert notification */}
        {actionAlert && (
          <div className={`pwa-toast ${actionAlert.type}`}>
            {actionAlert.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{actionAlert.message}</span>
          </div>
        )}

        {/* Playback Controls Overlay */}
        {playbackActive && (
          <div className="customer-playback-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                className="playback-btn play"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <span style={{ fontSize: '12px', fontWeight: 700 }}>
                Point {playbackIndex + 1} / {playbackPoints.length}
              </span>
            </div>

            <input
              type="range"
              min="0"
              max={playbackPoints.length - 1}
              value={playbackIndex}
              onChange={(e) => {
                const idx = Number(e.target.value);
                setPlaybackIndex(idx);
                const pt = playbackPoints[idx];
                if (pt && playbackMarkerRef.current) {
                  playbackMarkerRef.current.setLatLng([pt.latitude, pt.longitude]);
                }
              }}
              className="playback-slider"
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', fontWeight: 700 }}>
              <span style={{ color: '#4ade80' }}>● &lt;35 km/h</span>
              <span style={{ color: '#fbbf24' }}>● 35-60 km/h</span>
              <span style={{ color: '#f87171' }}>● &gt;60 km/h</span>
            </div>

            <div className="playback-controls">
              {[1, 2, 4, 8].map((spd) => (
                <button
                  key={spd}
                  type="button"
                  className={`playback-btn ${playbackSpeed === spd ? 'play' : ''}`}
                  onClick={() => setPlaybackSpeed(spd)}
                >
                  {spd}x
                </button>
              ))}
              <button type="button" className="playback-btn" onClick={stopPlayback}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* 3. LEAFLET MAP CANVAS */}
        <div className="customer-map-wrapper">
          <div
            ref={mapContainerCallback}
            className="customer-map-canvas"
            style={{ width: '100%', height: '100%', minHeight: '520px', position: 'relative' }}
          />

          {/* Floating HUD Telemetry & Today's Summary Card */}
          {selectedVehicle && (
            <div className="customer-telemetry-hud">
              <div className="hud-speedometer">
                <Gauge size={24} color="#0284c7" style={{ marginRight: '6px' }} />
                <span className="hud-speed-number">{Number(selectedVehicle.gps_speed || 0).toFixed(0)}</span>
                <span className="hud-speed-unit">KM/H</span>
              </div>

              <div className="hud-metrics-row">
                <div className="hud-metric-item">
                  <span className="hud-metric-label">Today's Distance</span>
                  <span className="hud-metric-value" style={{ color: '#0369a1' }}>
                    <Navigation size={13} color="#0284c7" />
                    <strong>28.4 KM</strong>
                  </span>
                </div>

                <div className="hud-metric-item">
                  <span className="hud-metric-label">Ignition</span>
                  <span className="hud-metric-value" style={{ color: Number(selectedVehicle.gps_ignition || 0) === 1 ? '#16a34a' : '#64748b' }}>
                    <Key size={14} />
                    {Number(selectedVehicle.gps_ignition || 0) === 1 ? 'ON' : 'OFF'}
                  </span>
                </div>

                <div className="hud-metric-item">
                  <span className="hud-metric-label">Battery</span>
                  <span className="hud-metric-value">
                    <BatteryCharging size={14} color="#0284c7" />
                    {selectedVehicle.battery_voltage || '12.6V'}
                  </span>
                </div>

                <div className="hud-metric-item">
                  <span className="hud-metric-label">Satellites</span>
                  <span className="hud-metric-value">
                    <Radio size={14} color="#16a34a" />
                    {selectedVehicle.gps_satellites || 14} Sats
                  </span>
                </div>

                <div className="hud-metric-item">
                  <span className="hud-metric-label">Security</span>
                  <span className="hud-metric-value" style={{ color: safeParkingActive ? '#16a34a' : '#64748b' }}>
                    <ShieldCheck size={14} />
                    {safeParkingActive ? 'Armed' : 'Disarmed'}
                  </span>
                </div>
              </div>

              <div className="hud-address-box">
                <span className="hud-metric-label" style={{ display: 'block', marginBottom: '2px' }}>Live Address</span>
                <MapPin size={13} color="#dc2626" style={{ display: 'inline', marginRight: '4px', verticalAlign: 'text-top' }} />
                {selectedVehicle.gps_address || 'Janpath Road, Connaught Place, New Delhi'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 4. MODAL: FULL DEVICE SPECS */}
      {showSpecModal && selectedVehicle && (
        <div className="device-spec-modal-overlay" onClick={() => setShowSpecModal(false)}>
          <div className="device-spec-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-spec-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="modal-spec-icon-box">
                  <Cpu size={22} color="#0284c7" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', color: '#0f172a', fontWeight: 900 }}>
                    Device Passport: {selectedVehicle.vehicle_no}
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                    Hardware & Service Certificate
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowSpecModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-spec-body">
              <div className="spec-banner-box">
                <div className="spec-banner-item">
                  <span className="spec-banner-label">Subscription Status</span>
                  <span
                    className="expiry-status-chip"
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      color: getExpiryStatus(selectedVehicle.renewal_date).color,
                      background: getExpiryStatus(selectedVehicle.renewal_date).bg
                    }}
                  >
                    {getExpiryStatus(selectedVehicle.renewal_date).label}
                  </span>
                </div>
                <div className="spec-banner-item">
                  <span className="spec-banner-label">Renewal Due Date</span>
                  <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>
                    {formatDateOnly(selectedVehicle.renewal_date)}
                  </strong>
                </div>
                <div className="spec-banner-item">
                  <span className="spec-banner-label">Installation Date</span>
                  <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>
                    {formatDateTime(selectedVehicle.installation_date || selectedVehicle.created_at)}
                  </strong>
                </div>
              </div>

              <div className="spec-details-table">
                <div className="spec-row">
                  <span className="spec-key">Vehicle Plate:</span>
                  <span className="spec-val"><strong>{selectedVehicle.vehicle_no}</strong></span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">Device Model:</span>
                  <span className="spec-val">{selectedVehicle.device_model || selectedVehicle.product_name || 'GT06 Smart GPS Tracker'}</span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">IMEI Number:</span>
                  <span className="spec-val font-mono">{selectedVehicle.imei || selectedVehicle.tracking_imei || '867543021984567'}</span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">SIM Card Number:</span>
                  <span className="spec-val font-mono">{selectedVehicle.sim_no || selectedVehicle.tracking_sim_no || '9876500001'}</span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">Driver Assigned:</span>
                  <span className="spec-val">{selectedVehicle.driver_name || 'Suresh Kumar'}</span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">Dealer / Admin:</span>
                  <span className="spec-val">{sellerName} ({supportMobile})</span>
                </div>
                <div className="spec-row">
                  <span className="spec-key">Current Voltage:</span>
                  <span className="spec-val">{selectedVehicle.battery_voltage || '12.6V'} (Normal)</span>
                </div>
              </div>
            </div>

            <div className="modal-spec-footer">
              <a
                href={`https://wa.me/${normalizeWhatsAppNumber(supportMobile)}?text=${encodeURIComponent(`Hello ${sellerName}, I would like to request subscription renewal for my GPS vehicle ${selectedVehicle.vehicle_no} (IMEI: ${selectedVehicle.imei}).`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-renew-wa-btn"
              >
                <Zap size={14} /> Request Instant Renewal via WhatsApp
              </a>
              <button
                type="button"
                className="modal-dismiss-btn"
                onClick={() => setShowSpecModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
